import { useTranslation } from 'react-i18next';
import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
    ChevronLeft, AtSign, Handshake, User, FileCode, Zap,
    ShieldCheck, CheckCircle2, XCircle, Send, AlertCircle, Clock
} from 'lucide-react';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { useNotify } from '@/context/NotificationContext';
import { usernameMarketApi } from '@/utils/api';
import { GlassTimePicker } from '@/components/ui/GlassTimePicker';

interface OfferDetailsPageProps {
    item: any;
    onBack: () => void;
    onNavigate: (page: string, data?: any) => void;
}

const fmt = (n: any) => {
    const v = Number(n);
    return isNaN(v) ? '0' : v.toLocaleString();
};

const GramIcon = ({ className }: { className?: string }) => (
    <svg viewBox="12 12 56 56" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
        <path fill="#30a1f5" d="M52.017 12.097H27.984c-3.201 0-4.802 0-6.25.448a10 10 0 0 0-3.496 1.909c-1.159.975-2.024 2.322-3.755 5.014l-7.64 11.884c-1.144 1.78-1.716 2.668-1.87 3.605a4.6 4.6 0 0 0 .263 2.45c.35.882 1.098 1.63 2.593 3.125L36.217 68.92c1.325 1.324 1.987 1.986 2.75 2.234a3.34 3.34 0 0 0 2.067 0c.763-.248 1.425-.91 2.75-2.234l28.388-28.388c1.495-1.495 2.243-2.243 2.593-3.125.31-.778.4-1.625.263-2.45-.155-.937-.727-1.826-1.87-3.605l-7.64-11.884c-1.73-2.692-2.596-4.039-3.756-5.014a10 10 0 0 0-3.496-1.91c-1.448-.447-3.048-.447-6.249-.447" />
        <path fill="#fff" d="M47.465 21.472c.39-1.055 1.883-1.055 2.274 0l2.698 7.292a1.6 1.6 0 0 0 .945.946l7.293 2.698c1.055.39 1.055 1.883 0 2.274l-7.293 2.698a1.6 1.6 0 0 0-.945.945l-2.698 7.293c-.39 1.055-1.883 1.055-2.274 0l-2.698-7.293a1.6 1.6 0 0 0-.946-.945l-7.292-2.698c-1.055-.39-1.055-1.883 0-2.274l7.292-2.698a1.6 1.6 0 0 0 .946-.946z" />
    </svg>
);

export default function OfferDetailsPage({ item, onBack, onNavigate }: OfferDetailsPageProps) {
    const { t } = useTranslation(['common', 'offers', 'profile', 'store']);
    const { impactOccurred, notificationOccurred } = useHapticFeedback();
    const { showNotification } = useNotify();

    const [asset, setAsset] = useState<any>(null);
    const [offers, setOffers] = useState<any[]>([]);
    const [history, setHistory] = useState<any[]>([]);
    const [highestExpired, setHighestExpired] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState<number | null>(null);

    const [placeMode, setPlaceMode] = useState(false);
    const [offerAmt, setOfferAmt] = useState('');
    const [offerDuration, setOfferDuration] = useState('24');

    // @ts-ignore
    const tgUser: any = window.Telegram?.WebApp?.initDataUnsafe?.user || {};
    const viewerId = String(tgUser?.id || '');

    const username = String(asset?.username || item?.username || item?.target_username || item?.card_name || '').replace(/^@/, '');
    const assetId = asset?.asset_id ?? item?.asset_id ?? item?.id ?? item?.listing_id ?? username;

    const ownerTelegramId = asset?.owner_telegram_id || item?.seller_id || item?.seller_telegram_id || null;
    const isOwner = Boolean(ownerTelegramId && viewerId && String(ownerTelegramId) === viewerId);

    const ownerName = asset?.owner_name || item?.owner_name || item?.seller_first_name || item?.seller_name || username || 'Unknown';
    const ownerPhoto = asset?.owner_photo || item?.owner_photo || item?.avatarUrl || item?.owner_avatar || item?.seller_avatar || '/icons/default-avatar.svg';
    const ownerUsername = asset?.owner_username || item?.owner_username || item?.seller_username || username;

    // Highest active offer
    const highestOffer = offers.length ? Math.max(...offers.map((o: any) => Number(o.amount) || 0)) : null;

    const load = useCallback(async () => {
        if (!assetId) {
            setAsset(item || {});
            setLoading(false);
            return;
        }
        try {
            const res = await usernameMarketApi.offersForAsset(assetId);
            const d = await res.json();
            if (res.ok && d.success) {
                setAsset(d.asset || item || null);
                setOffers(Array.isArray(d.offers) ? d.offers : []);
                setHistory(Array.isArray(d.history) ? d.history : []);
                setHighestExpired(d.highestExpired != null ? Number(d.highestExpired) : null);
            } else {
                setAsset(item || {});
            }
        } catch (e) {
            console.error('[offer-details]', e);
            setAsset(item || {});
        } finally {
            setLoading(false);
        }
    }, [assetId, item]);

    useEffect(() => { load(); }, [load]);

    const act = async (offerId: number, action: 'accept' | 'reject') => {
        if (!offerId || busyId) return;
        setBusyId(offerId);
        impactOccurred('medium');
        try {
            const res = await usernameMarketApi.offerAction(offerId, { action });
            const d = await res.json();
            if (res.ok && d.success) {
                notificationOccurred('success');
                showNotification(
                    action === 'accept' ? 'Offer Accepted' : 'Offer Rejected',
                    action === 'accept'
                        ? `@${username} sold to the bidder · ${fmt(d.pay ?? (offers.find(o => Number(o.offer_id) === offerId)?.amount))} GRAM`
                        : 'The offer was rejected and funds released.',
                    'success'
                );
                load();
            } else {
                notificationOccurred('error');
                showNotification('Failed', d.error || 'Could not update the offer', 'error');
            }
        } catch (e) {
            notificationOccurred('error');
            showNotification('Error', 'Connection failed', 'error');
        } finally {
            setBusyId(null);
        }
    };

    const sendOffer = async () => {
        const amt = Number(offerAmt);
        if (!username || !amt || amt <= 0) return showNotification('Offer', 'Enter a valid offer amount', 'error');
        setBusyId(-1);
        impactOccurred('heavy');
        try {
            const res = await usernameMarketApi.createOffer({
                username,
                amount: amt,
                offerType: 'offers',
                durationHours: Number(offerDuration) || 24
            });
            const d = await res.json();
            if (!res.ok || !d.success) throw new Error(d.error || 'Failed');
            notificationOccurred('success');
            showNotification('Offer Sent', `Your offer on @${username} (${fmt(amt)} GRAM) is pending.`, 'success');
            setPlaceMode(false);
            setOfferAmt('');
            load();
        } catch (e: any) {
            notificationOccurred('error');
            showNotification('Offer Failed', e.message || 'Could not send offer', 'error');
        } finally {
            setBusyId(null);
        }
    };

    const pendingOffers = offers.filter((o: any) => ['PENDING', 'COUNTERED'].includes(String(o.status).toUpperCase()));

    return (
        <div className="min-h-screen bg-tg-bg text-white overflow-x-hidden font-sans antialiased pb-24 relative">
            <div className="fixed top-[-10%] right-[-10%] w-[45%] h-[30%] bg-purple-500/10 blur-[120px] rounded-full z-0 pointer-events-none" />
            <div className="fixed bottom-[-10%] left-[-10%] w-[45%] h-[30%] bg-cyan-500/10 blur-[120px] rounded-full z-0 pointer-events-none" />

            {/* Header */}
            <nav className="h-20 pt-6 px-6 flex items-center justify-between sticky top-0 z-[100] bg-tg-bg/60 backdrop-blur-2xl border-b border-white/[0.03]">
                <button
                    onClick={() => { impactOccurred('light'); onBack(); }}
                    className="w-10 h-10 rounded-xl bg-white/[0.03] border border-white/[0.05] flex items-center justify-center hover:bg-white/[0.05] transition-colors"
                >
                    <ChevronLeft size={18} className="text-gray-400" />
                </button>
                <div className="text-center">
                    <div className="flex items-center gap-1.5 justify-center mb-0.5">
                        <span className="w-1 h-1 rounded-full animate-ping bg-purple-500" />
                        <span className="text-[7px] font-black text-purple-400/80 uppercase tracking-[0.4em]">{t('offers:offersStatus')}</span>
                    </div>
                    <h2 className="text-[11px] font-black uppercase tracking-widest text-white/90" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                        {pendingOffers.length ? `${pendingOffers.length} Active Offer${pendingOffers.length > 1 ? 's' : ''}` : 'Open to Offers'}
                    </h2>
                </div>
                <div className="w-10 h-10 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center">
                    <Handshake size={18} className="text-purple-300" />
                </div>
            </nav>

            <main className="max-w-md mx-auto px-4 pt-6 relative z-10 space-y-6">
                {/* HERO: Username */}
                <div className="flex flex-col items-center justify-center py-4">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500/30 to-pink-500/20 border border-purple-400/30 flex items-center justify-center mb-3">
                        <AtSign size={28} className="text-purple-200" />
                    </div>
                    <h1 className="text-3xl font-black tracking-tighter uppercase leading-none text-white drop-shadow-[0_2px_10px_rgba(0,0,0,0.5)]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                        @{username || 'unknown'}
                    </h1>
                    <div className="flex items-center justify-center gap-2 text-white/40 mt-3">
                        <span className="text-[10px] font-bold uppercase tracking-widest">{item?.asset_type || asset?.asset_type || 'Ownership'}</span>
                        <div className="w-1 h-1 bg-white/20 rounded-full" />
                        <span className="text-[10px] font-bold uppercase tracking-widest">Accepting Offers</span>
                    </div>
                </div>

                {/* Highest Offer / Price */}
                <motion.div
                    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                    className="p-5 rounded-[28px] glass-strong border border-white/10 shadow-[0_8px_30px_rgba(0,0,0,0.35)] flex items-center justify-between group"
                >
                    <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-2xl flex items-center justify-center bg-purple-500/20 border border-purple-500/20 shadow-lg shadow-purple-500/10">
                            <Zap size={20} className="text-purple-300 group-hover:scale-110 transition-transform" />
                        </div>
                        <div className="flex flex-col">
                            <span className="text-[10px] font-black text-purple-400 uppercase tracking-widest leading-none mb-1">
                                {highestOffer ? 'Highest Offer' : 'Offers Open'}
                            </span>
                            <div className="flex items-center gap-2">
                                {highestOffer ? (
                                    <>
                                        <GramIcon className="w-6 h-6" />
                                        <span className="text-2xl font-black tracking-tight text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{fmt(highestOffer)}</span>
                                        <span className="text-[10px] font-black text-white/40 uppercase ml-1">GRAM</span>
                                    </>
                                ) : highestExpired ? (
                                    <>
                                        <GramIcon className="w-6 h-6 opacity-70" />
                                        <span className="text-2xl font-black tracking-tight text-white/55" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{fmt(highestExpired)}</span>
                                        <span className="text-[10px] font-black text-white/30 uppercase ml-1">GRAM · ended</span>
                                    </>
                                ) : (
                                    <span className="text-lg font-black tracking-tight text-white/70" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{t('offers:noOffersYet')}</span>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="w-1 h-8 rounded-full bg-white/5" />
                    <div className="flex flex-col items-end">
                        <span className="text-[8px] font-black text-white/20 uppercase tracking-tighter">{t('offers:status')}</span>
                        <span className={`text-[10px] font-black uppercase tracking-widest ${highestOffer || !highestExpired ? 'text-green-400' : 'text-amber-400'}`}>
                            {pendingOffers.length ? 'Active' : highestExpired ? 'Ended' : 'Active'}
                        </span>
                    </div>
                </motion.div>

                {/* Asset Owner Section */}
                <div className="group relative bg-gradient-to-r from-white/[0.03] to-transparent border border-white/[0.05] rounded-[24px] p-4 flex items-center justify-between overflow-hidden">
                    <div className="flex items-center gap-4 relative z-10">
                        <div className="relative">
                            <img src={ownerPhoto} className="w-11 h-11 rounded-xl object-cover ring-1 ring-white/10" alt="Owner" />
                        </div>
                        <div className="flex flex-col">
                            <span className="text-[7px] font-black text-blue-500/60 uppercase tracking-widest">{t('offers:assetOwner')}</span>
                            <span className="text-[13px] font-bold text-white/90 group-hover:text-blue-400">{ownerName || 'Unknown'}</span>
                            <span className="text-[9px] font-mono text-gray-500">{ownerTelegramId ? `ID: ${ownerTelegramId}` : `ID: ${asset?.owner_user_id ?? item?.current_owner_id ?? 'Hidden'}`}</span>
                        </div>
                    </div>
                    <button
                        onClick={() => {
                            if (ownerTelegramId) {
                                impactOccurred('medium');
                                onNavigate('public_profile', {
                                    telegram_id: ownerTelegramId,
                                    username: ownerUsername || ownerName
                                });
                            }
                        }}
                        disabled={!ownerTelegramId}
                        className="flex items-center gap-2 px-3 py-1.5 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/20 rounded-xl transition-all active:scale-95 group disabled:opacity-30 relative z-10"
                    >
                        <User size={12} className="text-blue-400 group-hover:scale-110 transition-transform" />
                            <span className="text-[9px] font-black text-blue-400 uppercase tracking-widest">{t('offers:viewProfile')}</span>
                    </button>
                </div>

                {/* Smart Contract */}
                <div className="bg-[#070b14]/50 border border-white/[0.05] rounded-[28px] p-5 space-y-4 relative overflow-hidden">
                    <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
                    <div className="flex items-center justify-between group/item relative z-10">
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-xl bg-white/[0.03] border border-white/[0.05] flex items-center justify-center text-gray-400 group-hover/item:text-purple-400 transition-colors">
                                <FileCode size={18} />
                            </div>
                            <div className="flex flex-col">
                                <span className="text-[8px] font-black text-gray-500 uppercase tracking-widest">{t('offers:smartContract')}</span>
                                <span className="text-[11px] font-black text-white/90 tracking-wider">@{username} · {highestOffer ? `${fmt(highestOffer)} GRAM` : '0 GRAM'}</span>
                            </div>
                        </div>
                        <div className="px-2 py-1 bg-green-500/10 rounded-md border border-green-500/20 text-[7px] font-black text-green-400 uppercase tracking-widest">{t('offers:verified')}</div>
                    </div>
                    <div className="h-px bg-gradient-to-r from-transparent via-white/[0.05] to-transparent relative z-10" />
                    <div className="grid grid-cols-2 gap-4 relative z-10">
                        <div className="space-y-2">
                            <div className="flex items-center gap-2">
                                <span className="text-[8px] font-black text-gray-500 uppercase tracking-widest">{t('offers:minOffer')}</span>
                            </div>
                            <div className="flex items-center gap-1.5 px-1">
                                <GramIcon className="w-3 h-3 opacity-60" />
                                <span className="text-sm font-black text-white/60 tabular-nums tracking-tight">{t('offers:anyAmount')}</span>
                            </div>
                        </div>
                        <div className="space-y-2 border-l border-white/[0.05] pl-4">
                            <div className="flex items-center gap-2">
                                <span className="text-[8px] font-black text-gray-500 uppercase tracking-widest">{t('offers:commission')}</span>
                            </div>
                            <div className="flex items-center gap-1.5 px-1">
                                {(highestOffer ?? 0) > 0 && (highestOffer ?? 0) >= 0.2 ? (
                                    <>
                                        <GramIcon className="w-3 h-3 opacity-60" />
                                        <span className="text-sm font-black text-white/60 tabular-nums tracking-tight">{((highestOffer ?? 0) * 0.05).toFixed(2)} (5%)</span>
                                    </>
                                ) : (
                                    <span className="text-sm font-black text-white/30 tabular-nums tracking-tight">—</span>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="h-px bg-gradient-to-r from-transparent via-white/[0.05] to-transparent relative z-10" />
                    <div className="flex items-center justify-between relative z-10">
                        <span className="text-[8px] font-black text-gray-500 uppercase tracking-widest">{t('offers:contract')}</span>
                        <span className="text-[11px] font-black text-white/60 tracking-wider">GRAM-ERC721-V3</span>
                    </div>
                </div>

                {/* Offers List — people who made offers */}
                <div className="space-y-3">
                    <div className="flex items-center justify-between px-1">
                        <div className="flex items-center gap-2.5">
                            <ShieldCheck size={14} className="text-purple-500" />
                            <h3 className="text-[16px] font-black uppercase tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{t('offers:offersMade')}</h3>
                        </div>
                        <span className="text-[9px] font-black text-purple-400 uppercase tracking-widest">{pendingOffers.length} Active</span>
                    </div>

                    {loading ? (
                        <div className="p-8 text-center text-white/40 text-sm font-mono">{t('offers:loadingOffers')}</div>
                    ) : pendingOffers.length === 0 ? (
                        <div className="bg-white/[0.02] border border-white/[0.05] rounded-[24px] p-8 text-center space-y-3">
                            <AlertCircle size={24} className="text-white/20 mx-auto" />
                            <p className="text-sm text-white/50 font-semibold">No offers yet</p>
                            <p className="text-[11px] text-white/30">Be the first to place an offer on @{username}.</p>
                        </div>
                    ) : (
                        pendingOffers.map((o, i) => {
                            const oid = Number(o.offer_id);
                            const name = o.bidder_name || o.bidder_username || `@${o.bidder_username || `user_${o.bidder_id}`}`;
                            const photo = o.bidder_photo || '/icons/default-avatar.svg';
                            return (
                                <motion.div
                                    key={oid}
                                    initial={{ opacity: 0, y: 8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: i * 0.05 }}
                                    className="bg-white/[0.02] border border-white/[0.05] rounded-[24px] p-4"
                                >
                                    <div className="flex items-center gap-3">
                                        <img src={photo} className="w-10 h-10 rounded-xl object-cover ring-1 ring-white/10" alt="Bidder" />
                                        <div className="min-w-0 flex-1">
                                            <p className="text-[13px] font-bold text-white/90 truncate">{name}</p>
                                            <div className="flex items-center gap-2">
                                                <span className="text-[9px] font-mono text-gray-500">
                                                    {o.bidder_telegram_id ? `ID: ${o.bidder_telegram_id}` : `ID: ${o.bidder_id}`}
                                                </span>
                                                <span className="text-[9px] text-white/30">·</span>
                                                <span className="text-[9px] text-white/40">{new Date(o.created_at).toLocaleString()}</span>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <GramIcon className="w-4 h-4" />
                                            <span className="text-[15px] font-black text-white tabular-nums tracking-tight">{fmt(o.amount)}</span>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2 mt-3">
                                        <span className={`px-2 py-0.5 rounded-full text-[7px] font-black uppercase tracking-widest border ${String(o.status).toUpperCase() === 'COUNTERED' ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' : 'bg-purple-500/10 text-purple-300 border-purple-500/20'}`}>
                                            {String(o.status).toUpperCase() === 'COUNTERED' ? 'Countered' : 'Pending'}
                                        </span>
                                        {isOwner && (
                                            <div className="flex items-center gap-2 ml-auto">
                                                <button
                                                    onClick={() => act(oid, 'reject')}
                                                    disabled={busyId === oid || busyId === -1}
                                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-white/[0.05] hover:bg-white/[0.10] backdrop-blur-xl border border-white/[0.10] rounded-xl transition-all active:scale-95 disabled:opacity-50 text-[9px] font-black text-white/60 uppercase tracking-widest"
                                                >
                                                    <XCircle size={11} /> Reject
                                                </button>
                                                <button
                                                    onClick={() => act(oid, 'accept')}
                                                    disabled={busyId === oid || busyId === -1}
                                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-white/[0.06] hover:bg-white/[0.12] backdrop-blur-xl border border-green-500/25 rounded-xl transition-all active:scale-95 disabled:opacity-50 text-[9px] font-black text-green-400 uppercase tracking-widest"
                                                >
                                                    <CheckCircle2 size={11} className={busyId === oid ? 'animate-spin' : ''} /> Accept
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </motion.div>
                            );
                        })
                    )}
                </div>

                {/* Ended Offers History (expired / rejected / cancelled) */}
                {history.length > 0 && (
                    <div className="space-y-3">
                        <div className="flex items-center justify-between px-1">
                            <div className="flex items-center gap-2.5">
                                <XCircle size={14} className="text-amber-500" />
                                <h3 className="text-[14px] font-black uppercase tracking-tight text-white/70" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{t('offers:endedOffers')}</h3>
                            </div>
                            <span className="text-[9px] font-black text-amber-400 uppercase tracking-widest">{t('offers:closedCount_other', { count: history.length })}</span>
                        </div>
                        {history.map((o, i) => (
                            <motion.div
                                key={`h-${o.offer_id}-${i}`}
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: Math.min(i * 0.04, 0.4) }}
                                className="bg-white/[0.015] border border-white/[0.04] rounded-[20px] p-4 opacity-80"
                            >
                                <div className="flex items-center gap-3">
                                    <img src={o.bidder_photo || '/icons/default-avatar.svg'} className="w-9 h-9 rounded-xl object-cover ring-1 ring-white/5 opacity-80" alt="Bidder" />
                                    <div className="min-w-0 flex-1">
                                        <p className="text-[12px] font-bold text-white/60 truncate">{o.bidder_name || o.bidder_username || `user_${o.bidder_id}`}</p>
                                        <div className="flex items-center gap-1.5">
                                            <GramIcon className="w-3.5 h-3.5 opacity-60" />
                                            <span className="text-[14px] font-black text-white/70 tabular-nums tracking-tight">{fmt(o.amount)}</span>
                                        </div>
                                    </div>
                                    <span className={`px-2 py-0.5 rounded-full text-[7px] font-black uppercase tracking-widest border ${
                                        String(o.status).toUpperCase() === 'EXPIRED'
                                            ? 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                                            : String(o.status).toUpperCase() === 'REJECTED'
                                                ? 'bg-red-500/10 text-red-400 border-red-500/20'
                                                : 'bg-white/5 text-white/40 border-white/10'
                                    }`}>
                                        {String(o.status || 'closed').toLowerCase()}
                                        {o.expired_at ? ` · ${new Date(o.expired_at).toLocaleDateString()}` : ''}
                                    </span>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                )}
            </main>

            {/* Sticky Bottom Actions (owner sees Accept; others Place Offer) */}
            <div className="fixed bottom-0 inset-x-0 p-5 bg-tg-bg/80 backdrop-blur-lg border-t border-white/5 z-[80] max-w-md mx-auto">
                {isOwner ? (
                    <div className="flex items-center justify-center gap-2 px-4 py-3.5 bg-purple-500/10 border border-purple-500/20 rounded-2xl">
                        <Handshake size={16} className="text-purple-300" />
                        <span className="text-[11px] font-black text-purple-200 uppercase tracking-widest">
                            {pendingOffers.length ? 'Pick an offer to accept it' : 'Waiting for offers'}
                        </span>
                    </div>
                ) : placeMode ? (
                    <div className="space-y-2.5">
                        <div className="flex items-center gap-2 p-1.5 bg-white/[0.03] border border-white/[0.08] rounded-2xl">
                            <GramIcon className="w-5 h-5 ml-2.5 shrink-0" />
                            <input
                                type="number"
                                value={offerAmt}
                                onChange={(e) => setOfferAmt(e.target.value)}
                                placeholder={`Your offer for @${username}`}
                                className="w-full bg-transparent outline-none text-white font-black text-base placeholder:text-white/25 py-2 font-mono"
                            />
                        </div>

                        {/* Glass Offer Duration Picker (Search bar glass style + Days/Hours/Mins steppers + freeze warning) */}
                        <GlassTimePicker
                            durationHours={Number(offerDuration) || 24}
                            onChange={(h) => setOfferDuration(String(h))}
                            title="Offer Expiration Time"
                            showWarning={true}
                        />

                        <div className="flex gap-2">
                            <button
                                onClick={() => { setPlaceMode(false); setOfferAmt(''); }}
                                className="flex-1 py-3.5 bg-white/[0.04] hover:bg-white/[0.08] backdrop-blur-xl text-white/60 font-bold rounded-2xl border border-white/[0.10] transition-all active:scale-[0.98] text-sm"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={sendOffer}
                                disabled={busyId === -1 || !Number(offerAmt) || Number(offerAmt) <= 0}
                                className="flex-[2] py-3.5 bg-white/[0.06] hover:bg-white/[0.12] backdrop-blur-xl text-purple-300 font-bold rounded-2xl flex items-center justify-center gap-2 border border-purple-500/25 transition-all active:scale-[0.98] disabled:opacity-50 text-sm"
                            >
                                <Send size={16} className={busyId === -1 ? 'animate-pulse' : ''} />
                                {busyId === -1 ? 'Sending...' : 'Send Offer'}
                            </button>
                        </div>
                    </div>
                ) : (
                    <button
                        onClick={() => { impactOccurred('medium'); setPlaceMode(true); }}
                        className="w-full py-4 bg-white/[0.06] hover:bg-white/[0.12] backdrop-blur-xl text-white font-black rounded-2xl flex items-center justify-center gap-2 border border-purple-500/25 transition-all active:scale-[0.98]"
                    >
                        <Handshake size={20} />
                        <span className="text-lg tracking-wide uppercase" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{t('offers:placeOffer')}</span>
                    </button>
                )}
            </div>
        </div>
    );
}